import {isWorkflowBundleType} from '@frontapp/workflows/rules';
import {DateTime} from 'luxon';
import React, {FC, useCallback, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ConversationTeamStatusesEnum} from '../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {fetchSlaRuleBundlesAsyncAction} from '../../../../../../libs/shared/core/src/store/data/slaRuleBundleInfo/slaRuleBundleInfoActions';
import {getActiveLoadedSlaRuleBundles} from '../../../../../../libs/shared/core/src/store/data/slaRuleBundleInfo/slaRuleBundleInfoSelectors';
import {getHasSlaBreachTimersP3} from '../../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {DropdownBoxCoordinator} from '../../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {
  RelativeDateTimeFormatsEnum,
  RelativeDateTimeFormatter,
} from '../../../../../../libs/shared/ui/src/components/datetime/relativeDateTimeFormatter';
import {SimpleDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {useLinkNavigation} from '../../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {SlaBreachIndicator} from '../../../../../../libs/shared/ui/src/components/sla/slaBreachIndicator';
import {ComplexTooltipContent} from '../../../../../../libs/shared/ui/src/components/tooltips/complexTooltipContent';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {flexCenter} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {getEarliestSlaBundleBreach} from '../../../../../helpers/models/ruleBundleHelpers';
import {ConversationBaseModel} from '../../../../../models/api/conversationModel';
import {PermissionsEnum} from '../../../../../models/api/roleModel';
import {getHasPermission} from '../../../../../store/data/access/accessSelectors';
import {RulesSlaTimerEducationTipCoordinator} from '../../../../onboarding/sandbox/educationTips/rulesSlaTimerEducationTipCoordinator';

/*
 * Props.
 */

export interface ConversationTopbarSlaBreachIndicatorProps {
  conversation: ConversationBaseModel;
  isDisabled?: boolean;
}

/*
 * Styles.
 */

const StyledDropdownItemWrapperDiv = styled.div`
  ${flexCenter};
`;

const StyledComplexTooltipContent = styled(ComplexTooltipContent)`
  max-width: 247px;
`;

/*
 * Component.
 */

export const ConversationTopbarSlaBreachIndicator: FC<ConversationTopbarSlaBreachIndicatorProps> = ({
  conversation,
  isDisabled,
}) => {
  const shouldDisplaySlaBreachIndicator = useAppSelector(getHasSlaBreachTimersP3);
  const waitingSinceMs = conversation.orders?.waiting_since ?? 0;
  const hasPermission = useAppSelector(getHasPermission);
  const {navigateToLink} = useLinkNavigation();
  const slaRuleBundles = useAppSelector(getActiveLoadedSlaRuleBundles);
  const dispatch = useAppDispatch();

  // Fetch SLA Rule Bundles to filter out invalid SLA Timers.
  useEffect(() => {
    const hasSlaTimers = Object.keys(conversation.slaTimers).length > 0;
    if (!hasSlaTimers) {
      return;
    }

    dispatch(fetchSlaRuleBundlesAsyncAction());
  }, [conversation.slaTimers, dispatch]);

  const earliestSlaBreach = useMemo(() => {
    if (!slaRuleBundles) {
      return undefined;
    }

    return getEarliestSlaBundleBreach(conversation, slaRuleBundles);
  }, [conversation, slaRuleBundles]);

  if (!shouldDisplaySlaBreachIndicator || !getShouldDisplaySlaTimerForConversation(conversation)) {
    return null;
  }

  if (!earliestSlaBreach || waitingSinceMs === 0) {
    return null;
  }

  const isRuleAccessible = hasPermission(
    earliestSlaBreach.namespace,
    PermissionsEnum.RULE_UPDATE,
    /* adminOverride = */ true,
  );

  const onEditRule = () => {
    const link = isWorkflowBundleType(earliestSlaBreach.type)
      ? `/settings/${earliestSlaBreach.namespace}/workflows/edit/${earliestSlaBreach.id}`
      : `/settings/${earliestSlaBreach.namespace}/rules/recipes/${earliestSlaBreach.id}`;

    return navigateToLink(link);
  };

  const onLearnMore = () => {
    navigateToLink(SLA_LEARN_MORE_LINK);
  };

  return (
    <RulesSlaTimerEducationTipCoordinator conversationId={conversation.id}>
      <DropdownBoxCoordinator
        shouldDisablePadding
        renderDropdown={(popoverProps) =>
          renderDropdown(popoverProps, isRuleAccessible, onEditRule, onLearnMore)
        }
        isDisabled={isDisabled}
        renderButton={(isOpen) => (
          <TooltipCoordinator
            customMargin={4}
            render={() => (isOpen ? null : renderTooltip(earliestSlaBreach.breachTime))}
          >
            <ConversationTopbarSlaBreachIndicatorSimple
              waitingSinceMs={conversation.orders?.waiting_since ?? 0}
              breachTime={earliestSlaBreach.breachTime}
            />
          </TooltipCoordinator>
        )}
      />
    </RulesSlaTimerEducationTipCoordinator>
  );
};

export const ConversationTopbarSlaBreachIndicatorSimple: FC<{
  waitingSinceMs: number;
  breachTime: number;
  hasHover?: boolean;
  disableHover?: boolean;
}> = ({waitingSinceMs, breachTime, hasHover, disableHover}) => {
  // We want to update this state when the time component is refreshed, in order to re-render this
  // component, for the percentage to update.
  const [, setRefreshKey] = useState(0);

  const onRefresh = useCallback(() => {
    setRefreshKey((current) => current + 1);
  }, []);

  return (
    <SlaBreachIndicator
      percentage={computePercentage(waitingSinceMs, breachTime)}
      hasHover={hasHover}
      disableHover={disableHover}
    >
      <RelativeDateTimeFormatter
        value={breachTime}
        format={RelativeDateTimeFormatsEnum.SHORT_SIGNED}
        onRefresh={onRefresh}
      />
    </SlaBreachIndicator>
  );
};

/*
 * Helpers.
 */

function renderTooltip(slaBreach: number) {
  const isFuture = slaBreach > Date.now();
  const isLessThanMinute = Math.abs(DateTime.fromMillis(slaBreach).diffNow().as('minute')) < 1;

  return (
    <StyledComplexTooltipContent
      iconName="gearFilledSmall"
      title={
        <Trans id="conversation-topbar-sla-breach-indicator-tooltip-title">
          {isLessThanMinute ? (
            '1 minute'
          ) : (
            <RelativeDateTimeFormatter value={slaBreach} format={RelativeDateTimeFormatsEnum.LONG} />
          )}
          &nbsp;
          {isFuture ? 'to reply' : 'late to reply'}
        </Trans>
      }
      body={
        isFuture ? (
          <Trans id="conversation-topbar-sla-breach-indicator-tooltip-body-not-breached">
            An SLA rule applies to this conversation, which might lead to an SLA breach if not responded in
            time.
          </Trans>
        ) : (
          <Trans id="conversation-topbar-sla-breach-indicator-tooltip-body-breached">
            This conversation breached an SLA requirement because it hasn&apos;t been responded in time.
          </Trans>
        )
      }
    />
  );
}

function renderDropdown(
  popoverProps: PopoverTransferProps,
  isRuleAccessible: boolean,
  onEditRule: () => void,
  onLearnMore: () => void,
) {
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <SimpleDropdown {...popoverProps}>
      <SimpleDropdownItem isDisabled={!isRuleAccessible} onSelect={onEditRule}>
        Edit rule
      </SimpleDropdownItem>
      <SimpleDropdownItem onSelect={onLearnMore}>
        <StyledDropdownItemWrapperDiv>
          Learn more about SLAs&nbsp;
          <Icon name="externalLinkSmall" />
        </StyledDropdownItemWrapperDiv>
      </SimpleDropdownItem>
    </SimpleDropdown>
  );
}

function computePercentage(waitingSince: number, breachTime: number): number {
  const isBreachTimePast = Date.now() > breachTime;
  if (isBreachTimePast) {
    return 1;
  }

  const totalTime = breachTime - waitingSince;
  const timeElapsed = Date.now() - waitingSince;

  const percentage = Math.min(timeElapsed / totalTime, 1);

  // If totalTime === 0, the percentage will not be finite.
  if (!isFinite(percentage)) {
    return 1;
  }

  return percentage;
}

function getShouldDisplaySlaTimerForConversation(conversation: ConversationBaseModel): boolean {
  const isSnoozed = conversation.reminders.some((conv) => conv.isTeamReminder);
  if (!isSnoozed && conversation.teamStatus !== ConversationTeamStatusesEnum.OPEN) {
    return false;
  }

  return true;
}

const SLA_LEARN_MORE_LINK = 'https://help.front.com/en/articles/2107';
